import { useEffect, useRef } from "react";
import useCountdown from "@/hooks/useCountdown";
import { tickBeep, endBeep } from "@/utils/timerSounds";
import { useTheme } from "@/context/ThemeContext";
import { ss, offsetTransform } from "@/utils/displayStyle";

/**
 * Reusable countdown display.
 *
 * Color rules (per spec — user requirement 2.2):
 *   - 30s..11s → CONFIGURED color from display_styles[screen].timer_number
 *                (falls back to white for "huge" variant, foreground otherwise)
 *   - 10s..6s  → orange warning
 *   - 5s..0s   → red critical
 *
 * Plays beeps at 3, 2, 1 (tick) and 0 (end).
 *
 * Variants:
 *   - "huge"    : public big-screen mode (large text, wired to display_styles)
 *   - "panel"   : manager panel
 *   - "compact" : judge view
 *
 * When rendered on the big screen the caller passes `screen="battle_run"`
 * or `screen="circle_run"` so the timer picks up the user's per-screen
 * `timer_number` styling (color/size/offset).
 */
export const TimerDisplay = ({ timer, variant = "panel", playSounds = true, showProgress = true, sizeMultiplier = 100, screen = null }) => {
  const { theme } = useTheme();
  const { elapsedMs, secondsLeft } = useCountdown(timer);
  const lastSecondRef = useRef(null);

  // Play sounds at 3/2/1/0
  useEffect(() => {
    if (!playSounds) return;
    if (!timer || timer.status !== "running") {
      lastSecondRef.current = null;
      return;
    }
    if (lastSecondRef.current === secondsLeft) return;
    const prev = lastSecondRef.current;
    lastSecondRef.current = secondsLeft;
    if (prev === null) return;
    if (secondsLeft === 0 && prev !== 0) {
      endBeep();
    } else if (secondsLeft >= 1 && secondsLeft <= 3 && prev > secondsLeft) {
      tickBeep();
    }
  }, [secondsLeft, playSounds, timer]);

  if (!timer) return null;

  const duration = timer.duration_ms || 30000;
  const progress = duration ? (elapsedMs / duration) * 100 : 0;

  // Read the configured timer_number style (only applies on the big
  // screen — variant="huge"). Callers on the director/judge panels can
  // pass screen=null and get the legacy behaviour.
  const timerS = screen ? ss(theme, screen, "timer_number") : null;
  const configuredColor = timerS?.color;
  const configuredSize = timerS?.size ?? 1;
  const configuredOffset = timerS ? offsetTransform(timerS) : "";

  // New color logic per user spec 2.2 — 30-11 configured / 10-6 orange / 5-0 red.
  let color;
  if (secondsLeft <= 5) {
    color = "#dc2626"; // red
  } else if (secondsLeft <= 10) {
    color = "#f97316"; // orange
  } else if (configuredColor) {
    color = configuredColor;
  } else {
    color = variant === "huge" ? "#ffffff" : undefined; // fall back to tailwind text-foreground otherwise
  }

  let sizeClass = "text-7xl";
  let inlineFontSize = null;
  if (variant === "huge") {
    // Combine screen sizeMultiplier and the configured size factor.
    const baseVw = 18; // ~16rem at 1080p
    const scaled = (baseVw * sizeMultiplier * configuredSize) / 100;
    inlineFontSize = `${scaled}vw`;
    sizeClass = "leading-none";
  } else if (variant === "compact") {
    sizeClass = "text-4xl";
  }

  // Display value: show total duration when idle/pre_start, actual remaining otherwise.
  let display = secondsLeft;
  if (timer.status === "idle" || timer.status === "pre_start") {
    display = Math.ceil((duration / 1000));
  }

  const textColorClass = color ? "" : (variant === "huge" ? "text-white" : "text-foreground");

  return (
    <div className="flex flex-col items-center gap-3 select-none" data-testid={`timer-display-${variant}`}>
      <div
        className={`font-black tabular-nums ${textColorClass} ${sizeClass} transition-colors`}
        style={{
          fontVariantNumeric: "tabular-nums",
          textShadow: variant === "huge" ? (timerS?.textShadow ?? '0 4px 24px rgba(0,0,0,0.55)') : 'none',
          ...(inlineFontSize ? { fontSize: inlineFontSize } : {}),
          ...(color ? { color } : {}),
          ...(configuredOffset ? { transform: configuredOffset } : {}),
        }}
        data-testid="timer-seconds-left"
      >
        {String(display).padStart(2, "0")}
      </div>
      {showProgress && (
        <div className="w-full max-w-md h-3 bg-muted rounded-full overflow-hidden" data-testid="timer-progress-track">
          <div
            className={`h-full transition-[width] duration-100 ease-linear ${secondsLeft <= 5 ? 'bg-red-600' : secondsLeft <= 10 ? 'bg-orange-500' : 'bg-primary'}`}
            style={{ width: `${progress}%` }}
            data-testid="timer-progress-bar"
          />
        </div>
      )}
    </div>
  );
};

export default TimerDisplay;
